import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
    static values = {
        apiKey: String,
    };

    static targets = [
        "input",
        "address",
        "number",
        "country",
        "state",
        "city",
        "district",
        "postalCode",
        "lat",
        "lng",
    ];

    declare apiKeyValue: string;
    declare inputTarget: HTMLInputElement;
    declare addressTarget: HTMLInputElement;
    declare numberTarget: HTMLInputElement;
    declare countryTarget: HTMLInputElement;
    declare stateTarget: HTMLInputElement;
    declare cityTarget: HTMLInputElement;
    declare districtTarget: HTMLInputElement;
    declare postalCodeTarget: HTMLInputElement;
    declare latTarget: HTMLInputElement;
    declare lngTarget: HTMLInputElement;

    declare hasInputTarget: boolean;
    declare hasAddressTarget: boolean;
    declare hasNumberTarget: boolean;
    declare hasCountryTarget: boolean;
    declare hasStateTarget: boolean;
    declare hasCityTarget: boolean;
    declare hasDistrictTarget: boolean;
    declare hasPostalCodeTarget: boolean;
    declare hasLatTarget: boolean;
    declare hasLngTarget: boolean;

    connect(): void {
        if (!this.apiKeyValue) {
            return;
        }

        this.loadGoogleMapsApi();
    }

    fillFromSearch(): void {
        if (!this.hasInputTarget) {
            return;
        }

        const fallbackAddress = this.parseAddressLine(this.inputTarget.value);

        if (this.hasAddressTarget && fallbackAddress.route) {
            this.addressTarget.value = fallbackAddress.route;
        }

        if (this.hasNumberTarget && fallbackAddress.streetNumber) {
            this.numberTarget.value = fallbackAddress.streetNumber;
        }
    }

    private loadGoogleMapsApi(): void {
        if (typeof google !== "undefined" && google.maps?.places) {
            this.initAutocomplete();
            return;
        }

        const callbackName = "__googleMapsReady";
        const readyAttr    = "data-google-maps-ready";

        if (!(window as any)[callbackName]) {
            (window as any)[callbackName] = () => {
                document
                    .querySelectorAll(`[${readyAttr}]`)
                    .forEach(el =>
                        el.dispatchEvent(new CustomEvent("google-maps:ready"))
                    );
            };
        }

        this.element.setAttribute(readyAttr, "");
        this.element.addEventListener(
            "google-maps:ready",
            () => this.initAutocomplete(),
            { once: true }
        );

        if (!document.querySelector("script[data-google-maps]")) {
            const script = document.createElement("script");
            script.src = `https://maps.googleapis.com/maps/api/js?key=${this.apiKeyValue}&libraries=places&loading=async&callback=${callbackName}`;
            script.dataset.googleMaps = "true";
            document.head.appendChild(script);
        }
    }

    private initAutocomplete(): void {
        if (!this.hasInputTarget) {
            return;
        }

        const autocomplete = new google.maps.places.Autocomplete(
            this.inputTarget,
            { types: ["geocode"] }
        );

        autocomplete.addListener("place_changed", () => {
            const place = autocomplete.getPlace();

            if (!place.geometry) {
                return;
            }

            const components = this.parseComponents(
                place.address_components || []
            );
            const fallbackAddress = this.parseAddressFallback(place);
            const route = components.route || fallbackAddress.route;
            const streetNumber = components.streetNumber || fallbackAddress.streetNumber;

            if (this.hasAddressTarget) {
                this.addressTarget.value = route;
            }
            if (this.hasNumberTarget) {
                this.numberTarget.value = streetNumber;
            }
            if (this.hasCountryTarget) {
                this.countryTarget.value = components.country;
            }
            if (this.hasStateTarget) {
                this.stateTarget.value = components.state;
            }
            if (this.hasCityTarget) {
                this.cityTarget.value = components.city;
            }
            if (this.hasDistrictTarget) {
                this.districtTarget.value = components.district;
            }
            if (this.hasPostalCodeTarget) {
                this.postalCodeTarget.value = components.postalCode;
            }
            if (place.geometry.location) {
                if (this.hasLatTarget) {
                    this.latTarget.value = String(
                        place.geometry.location.lat()
                    );
                }
                if (this.hasLngTarget) {
                    this.lngTarget.value = String(
                        place.geometry.location.lng()
                    );
                }
            }
        });
    }

    private parseComponents(
        components: google.maps.GeocoderAddressComponent[]
    ): Record<string, string> {
        const result: Record<string, string> = {
            route: "",
            streetNumber: "",
            country: "",
            state: "",
            city: "",
            district: "",
            postalCode: "",
        };

        for (const component of components) {
            const types = component.types;

            if (types.includes("route")) {
                result.route = component.long_name;
            } else if (types.includes("street_number")) {
                result.streetNumber = component.long_name;
            } else if (types.includes("country")) {
                result.country = component.long_name;
            } else if (types.includes("administrative_area_level_1")) {
                result.state = component.long_name;
            } else if (
                types.includes("locality") ||
                types.includes("administrative_area_level_2")
            ) {
                result.city = component.long_name;
            } else if (types.includes("sublocality_level_1")) {
                result.district = component.long_name;
            } else if (types.includes("postal_code")) {
                result.postalCode = component.long_name;
            }
        }

        return result;
    }

    private parseAddressFallback(place: google.maps.places.PlaceResult): Record<string, string> {
        const address = place.formatted_address || place.name || "";
        const firstPart = address.split(",")[0]?.trim() || "";

        return this.parseAddressLine(firstPart);
    }

    private parseAddressLine(address: string): Record<string, string> {
        const firstPart = address.split(",")[0]?.trim() || "";
        const match = firstPart.match(/^(.+?)\s+(\d+[a-zA-Z0-9\-\/]*)$/);

        if (!match) {
            return {
                route: firstPart,
                streetNumber: "",
            };
        }

        return {
            route: match[1].trim(),
            streetNumber: match[2].trim(),
        };
    }

}
